import styled, { css } from 'styled-components'
import { COLORS, SPACING, BORDER_RADIUS, TYPOGRAPHY, FONT_WEIGHT } from '../../../../styles/theme'

export const Card = styled.div<{ $deleted?: boolean }>`
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: ${BORDER_RADIUS.lg};
  padding: ${SPACING.xl};
  page-break-inside: avoid;
  overflow: hidden;
  opacity: ${(p) => (p.$deleted ? 0.75 : 1)};
`

export const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACING.md};
  margin-bottom: ${SPACING.lg};
`

export const QuestionMeta = styled.div`
  flex: 1;
`

export const QuestionNumber = styled.span`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

export const QuestionText = styled.h3`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-primary);
  margin: ${SPACING.xs} 0 0 0;
  line-height: 1.4;
`

export const TypeBadge = styled.span<{ $color: string }>`
  flex-shrink: 0;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${(p) => p.$color};
  background: ${(p) => p.$color}18;
  padding: 2px ${SPACING.sm};
  border-radius: ${BORDER_RADIUS.full};
  white-space: nowrap;
`

export const SourceBadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACING.xs};
  margin-top: ${SPACING.xs};
`

export const SourceBadge = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border);
  padding: 1px ${SPACING.sm};
  border-radius: ${BORDER_RADIUS.full};
  max-width: 240px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const DeletedBadge = styled.span`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${COLORS.gray500};
  background: ${COLORS.gray100};
  padding: 2px ${SPACING.sm};
  border-radius: ${BORDER_RADIUS.full};
  white-space: nowrap;
`

export const NoAnswer = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  font-style: italic;
  padding: ${SPACING.md} 0;
`

export const QuoteCard = styled.div`
  border-left: 3px solid rgba(110, 83, 249, 0.25);
  padding: ${SPACING.md} ${SPACING.lg};
  background: var(--color-bg-secondary);
  border-radius: 0 ${BORDER_RADIUS.md} ${BORDER_RADIUS.md} 0;
  font-size: ${TYPOGRAPHY.body.fontSize};
  line-height: 1.55;
  color: var(--color-text-primary);
  white-space: pre-wrap;
`

export const ScaleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.md};
`

export const ScaleDots = styled.div`
  display: flex;
  gap: ${SPACING.xs};
`

export const ScaleDot = styled.div<{ $active: boolean; $color: string }>`
  width: 28px;
  height: 28px;
  border-radius: ${BORDER_RADIUS.full};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  border: 1px solid var(--color-border);
  background: ${(p) => (p.$active ? p.$color : 'var(--color-bg-secondary)')};
  color: ${(p) => (p.$active ? '#fff' : 'var(--color-text-secondary)')};
  ${(p) =>
    p.$active &&
    css`
      border-color: ${p.$color};
    `}
`

export const ScaleValueLabel = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-primary);
`

export const YesNoPill = styled.div<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.xs};
  padding: ${SPACING.xs} ${SPACING.md};
  background: ${(p) => p.$color}1f;
  color: ${(p) => p.$color};
  border: 1px solid ${(p) => p.$color}55;
  border-radius: ${BORDER_RADIUS.full};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
`

export const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm};
`

export const OptionRow = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: ${SPACING.md};
  padding: ${SPACING.sm} ${SPACING.md};
  border-radius: ${BORDER_RADIUS.md};
  border: 1px solid ${(p) => (p.$selected ? `${COLORS.primary}55` : 'var(--color-border)')};
  background: ${(p) => (p.$selected ? `${COLORS.primary}10` : 'transparent')};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: ${(p) => (p.$selected ? 'var(--color-text-primary)' : 'var(--color-text-secondary)')};
  font-weight: ${(p) => (p.$selected ? FONT_WEIGHT.semibold : FONT_WEIGHT.regular)};
`

export const OptionMarker = styled.div<{ $selected: boolean; $shape: 'circle' | 'square' }>`
  width: 16px;
  height: 16px;
  border-radius: ${(p) => (p.$shape === 'circle' ? '50%' : '4px')};
  border: 1.5px solid ${(p) => (p.$selected ? COLORS.primary : 'var(--color-border)')};
  background: ${(p) => (p.$selected ? COLORS.primary : 'transparent')};
  flex-shrink: 0;
  position: relative;

  ${(p) =>
    p.$selected &&
    p.$shape === 'circle' &&
    css`
      &::after {
        content: '';
        position: absolute;
        inset: 4px;
        background: #fff;
        border-radius: 50%;
      }
    `}

  ${(p) =>
    p.$selected &&
    p.$shape === 'square' &&
    css`
      &::after {
        content: '';
        position: absolute;
        left: 4px;
        top: 1px;
        width: 4px;
        height: 8px;
        border: solid #fff;
        border-width: 0 2px 2px 0;
        transform: rotate(45deg);
      }
    `}
`
